import { getApiBaseUrl } from '../config/apiBase';

/** @typedef {'go_pressed'|'dns_check_start'|'dns_check_done'|'preprocess_start'|'preprocess_done'|'handle_search_start'|'stream_gate_start'|'stream_gate_done'|'sse_open'|'sse_first_event'|'scan_complete'} SearchTraceStep */

let activeTrace = null;

function newTraceId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `trace-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function redactQueryHint(query) {
  const raw = String(query || '').trim();
  if (!raw) return '';
  return raw.length > 120 ? `${raw.slice(0, 117)}...` : raw;
}

function postTracePayload(payload) {
  const base = getApiBaseUrl().replace(/\/$/, '');
  const url = `${base}/client-search-trace`;
  const body = JSON.stringify(payload);
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'application/json' });
      if (navigator.sendBeacon(url, blob)) return;
    }
  } catch {
    /* fall through */
  }
  fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    keepalive: true,
    credentials: 'same-origin',
  }).catch(() => {});
}

/**
 * Start a new frontend search timing trace (hero Go or tab Search).
 *
 * @param {{ query?: string, tab?: string, source?: string }} [opts]
 * @returns {string} trace id
 */
export function beginSearchTrace(opts = {}) {
  const traceId = newTraceId();
  activeTrace = {
    traceId,
    t0: typeof performance !== 'undefined' ? performance.now() : Date.now(),
    tab: String(opts.tab || '').trim() || 'unknown',
    source: String(opts.source || 'hero').trim() || 'hero',
    query: String(opts.query || '').trim(),
    endpoint: '',
  };
  markSearchTrace('go_pressed', { detail: activeTrace.source });
  return traceId;
}

/**
 * @param {string} key
 * @param {string} value
 */
export function setActiveSearchTraceMeta(key, value) {
  if (!activeTrace || !key) return;
  activeTrace[key] = value;
}

/**
 * @param {SearchTraceStep} step
 * @param {{ detail?: string, endpoint?: string, ms?: number }} [extra]
 */
export function markSearchTrace(step, extra = {}) {
  if (!activeTrace) return;
  const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
  const deltaMs = Math.max(0, Math.round(now - activeTrace.t0));
  if (extra.endpoint) activeTrace.endpoint = extra.endpoint;
  postTracePayload({
    trace_id: activeTrace.traceId,
    step,
    delta_ms: typeof extra.ms === 'number' ? extra.ms : deltaMs,
    tab: activeTrace.tab,
    source: activeTrace.source,
    query_hint: redactQueryHint(activeTrace.query),
    endpoint: extra.endpoint || activeTrace.endpoint || undefined,
    detail: extra.detail ? String(extra.detail).slice(0, 240) : undefined,
  });
}

export function clearActiveSearchTrace() {
  activeTrace = null;
}

export function getActiveSearchTraceId() {
  return activeTrace?.traceId || null;
}
